import React from 'react';
import {
  Dialog,
  AppBar, 
  Toolbar, 
  IconButton,
  Typography,
  Box,
  Grid, 
  Divider,
  Paper,
  useTheme,
  alpha,
  Button, 
  LinearProgress 
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import StraightenIcon from '@mui/icons-material/Straighten'; 
import CheckCircleIcon from '@mui/icons-material/CheckCircle'; 
import ErrorIcon from '@mui/icons-material/Error'; 
import HistoryIcon from '@mui/icons-material/History'; 
import WarningAmberIcon from '@mui/icons-material/WarningAmber'; 
import DirectionsIcon from '@mui/icons-material/Directions'; // Icon for Bound

// Helper to display detail rows like in the image
const DetailRow = ({ icon: Icon, label, value, sx = {} }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, ...sx }}>
    <Icon fontSize="small" sx={{ color: (theme) => theme.palette.text.secondary, mr: 1.5, width: 20, textAlign: 'center' }} />
    <Typography variant="body2" color="text.secondary" sx={{ minWidth: '100px' }}> {/* Adjust minWidth as needed */}
      {label}:
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: 500, ml: 1 }}>
      {value || '-'}
    </Typography>
  </Box>
);

// Helper for Detection Class row with Change button
const DetailRowWithButton = ({ icon: Icon, label, value, onButtonClick, sx = {} }) => (
   <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, ...sx }}>
    <Icon fontSize="small" sx={{ color: (theme) => theme.palette.text.secondary, mr: 1.5, width: 20, textAlign: 'center' }} />
    <Typography variant="body2" color="text.secondary" sx={{ minWidth: '100px' }}>
      {label}:
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: 500, ml: 1, mr: 1 }}>
      {value || '-'}
    </Typography>
    <Button size="small" variant="text" onClick={onButtonClick} sx={{ textTransform: 'none', p: 0, minWidth: 'auto' }}>
      Change
    </Button>
  </Box>
);


const DetectionDetailModal = ({ open, onClose, detection }) => {
  const theme = useTheme();
  const detailPanelWidth = 380; 

  if (!detection) {
    return null; 
  }

  // --- Placeholder Data ---
  const placeholderData = {
    regionSection: 'South - Section A', // Example
    width: '68 cm',
    length: '75 cm',
    area: '0.51 m²',
    confidence: '87%',
    elapsedTime: '17 hours',
    tempFixDue: '48 hours (2d 0h)',
    permFixDue: '168 hours (7d 0h)',
    responseTimeProgress: 35, // Example percentage
  };
  // --- End Placeholder Data ---


  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="lg" 
      fullWidth 
      PaperProps={{ 
        sx: { 
          borderRadius: 2, 
          height: '85vh', 
          overflow: 'hidden' 
        } 
      }}
    >
      {/* AppBar for Title and Close Button */}
      <AppBar position="static" color="default" elevation={1} sx={{ bgcolor: 'background.paper' }}>
        <Toolbar variant="dense">
          <Typography variant="h6" component="div" sx={{ flexGrow: 1, fontWeight: 500 }}>
            Detection Details #{detection.id} {/* Use # like image */}
          </Typography>
          <IconButton 
            edge="end" 
            color="inherit" 
            onClick={onClose} 
            aria-label="close"
            sx={{ 
              '&:hover': { 
                bgcolor: alpha(theme.palette.error.main, 0.1),
                color: theme.palette.error.main
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Main Content Area (Flex Layout) */}
      <Box sx={{ display: 'flex', height: 'calc(85vh - 48px)' }}> 
        
        {/* Left Panel: Details */}
        <Paper
          elevation={0}
          square 
          sx={{
            width: detailPanelWidth,
            flexShrink: 0,
            borderRight: `1px solid ${theme.palette.divider}`,
            overflowY: 'auto', 
            p: 2.5, 
          }}
        >
          {/* General Information Section */}
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, mb: 1.5 }}>
            General Information
          </Typography>
          <DetailRow icon={CalendarTodayIcon} label="Date" value={detection.detected} />
          <DetailRow icon={AccessTimeIcon} label="Time" value={"11:24:22"} /> {/* Placeholder Time */}
          <Divider sx={{ my: 1.5 }} />
          <DetailRow icon={LocationOnIcon} label="Network" value={detection.network} />
          <DetailRow icon={LocationOnIcon} label="Location" value={detection.location} />
          <DetailRow icon={LocationOnIcon} label="Region/Section" value={placeholderData.regionSection} />
          <DetailRow icon={DirectionsIcon} label="Bound" value={detection.bound} /> {/* Added Bound */}
          <Divider sx={{ my: 1.5 }} />
          <DetailRow icon={StraightenIcon} label="Width" value={placeholderData.width} />
          <DetailRow icon={StraightenIcon} label="Length" value={placeholderData.length} />
          <DetailRow icon={StraightenIcon} label="Area" value={placeholderData.area} />
           <Divider sx={{ my: 1.5 }} />
          <DetailRow icon={CheckCircleIcon} label="Confidence Level" value={placeholderData.confidence} />
          <DetailRowWithButton 
            icon={ErrorIcon} 
            label="Detection Class" 
            value={detection.detection} 
            onButtonClick={() => console.log("Change class clicked")} // Placeholder action
          />
          
          <Divider sx={{ my: 2 }} />

          {/* Response Time Section */}
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, mb: 1.5 }}>
            Response Time
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
             <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <HistoryIcon fontSize="small" sx={{ color: theme.palette.info.main, mr: 1.5 }} />
                <Typography variant="body2" color="text.secondary">Elapsed Time:</Typography>
             </Box>
             <Typography variant="body2" sx={{ fontWeight: 500 }}>{placeholderData.elapsedTime}</Typography>
          </Box>
           <LinearProgress 
              variant="determinate" 
              value={placeholderData.responseTimeProgress} 
              sx={{ height: 8, borderRadius: 4, mb: 2, bgcolor: alpha(theme.palette.primary.main, 0.2) }} 
            />
          <DetailRow icon={WarningAmberIcon} label="Temporary Fix Due" value={placeholderData.tempFixDue} sx={{ mb: 1.5 }}/>
          <DetailRow icon={ErrorIcon} label="Permanent Fix Due" value={placeholderData.permFixDue} />

        </Paper>

        {/* Right Panel: Image */}
        <Box
          sx={{
            flexGrow: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: alpha(theme.palette.common.black, 0.05), 
            overflow: 'hidden', 
            p: 0, 
          }}
        >
          <Box
            component="img"
            src="/Sample_Detectio2n.jpg" 
            alt={`Detection ${detection.id}`}
            sx={{
              display: 'block',
              width: '100%', 
              height: '100%', 
              objectFit: 'cover', 
            }}
          />
        </Box>
      </Box>
    </Dialog>
  );
};

export default DetectionDetailModal;